import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateWishInput, UpdateWishInput } from '@family-wishlist/shared';
import { toast } from 'sonner';
import { wishesApi, type OwnerStatus } from './wishes.api';
import { ApiError } from '@/lib/api';
import { useI18n } from '@/i18n/i18n';

const LIST_KEY = (status: OwnerStatus) => ['wishes', status] as const;

function invalidateAll(client: ReturnType<typeof useQueryClient>): void {
  void client.invalidateQueries({ queryKey: ['wishes'] });
}

function toastError(err: unknown, fallback: string): void {
  if (err instanceof ApiError) toast.error(err.message);
  else toast.error(fallback);
}

export function useWishes(status: OwnerStatus) {
  return useQuery({
    queryKey: LIST_KEY(status),
    queryFn: () => wishesApi.list(status),
    staleTime: 10_000,
  });
}

export function useCreateWish() {
  const client = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (input: CreateWishInput) => wishesApi.create(input),
    onSuccess: () => {
      invalidateAll(client);
      toast.success(t('toast.wishAdded'));
    },
    onError: (err) => toastError(err, t('toast.genericError')),
  });
}

export function useUpdateWish() {
  const client = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (vars: { id: string; input: UpdateWishInput }) =>
      wishesApi.update(vars.id, vars.input),
    onSuccess: () => {
      invalidateAll(client);
      toast.success(t('toast.saved'));
    },
    onError: (err) => toastError(err, t('toast.genericError')),
  });
}

export function useArchiveWish() {
  const client = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (id: string) => wishesApi.archive(id),
    onSuccess: () => {
      invalidateAll(client);
      toast.success(t('toast.archived'));
    },
    onError: (err) => toastError(err, t('toast.genericError')),
  });
}

export function useCompleteWish() {
  const client = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (id: string) => wishesApi.complete(id),
    onSuccess: () => {
      invalidateAll(client);
      toast.success(t('toast.fulfilled'));
    },
    onError: (err) => toastError(err, t('toast.genericError')),
  });
}

export function useDeleteWish() {
  const client = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (id: string) => wishesApi.remove(id),
    onSuccess: () => {
      invalidateAll(client);
      toast.success(t('toast.deleted'));
    },
    onError: (err) => toastError(err, t('toast.genericError')),
  });
}

export function useRestoreWish() {
  const client = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (id: string) => wishesApi.restore(id),
    onSuccess: () => {
      invalidateAll(client);
      toast.success(t('toast.restored'));
    },
    onError: (err) => toastError(err, t('toast.genericError')),
  });
}

export function useDuplicateWish() {
  const client = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (id: string) => wishesApi.duplicate(id),
    onSuccess: () => {
      invalidateAll(client);
      toast.success(t('toast.duplicated'));
    },
    onError: (err) => toastError(err, t('toast.genericError')),
  });
}

export function useUploadWishImage() {
  const client = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (vars: { id: string; file: File }) => wishesApi.uploadImage(vars.id, vars.file),
    onSuccess: () => {
      invalidateAll(client);
      toast.success(t('toast.imageUpdated'));
    },
    onError: (err) => toastError(err, t('toast.genericError')),
  });
}

export function useRefreshOg() {
  const client = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (id: string) => wishesApi.refreshOg(id),
    onSuccess: () => {
      invalidateAll(client);
      toast.success(t('toast.imageRefreshed'));
    },
    onError: (err) => toastError(err, t('toast.imageFetchFailed')),
  });
}

export function useResetWishImage() {
  const client = useQueryClient();
  const { t } = useI18n();
  return useMutation({
    mutationFn: (id: string) => wishesApi.deleteImage(id),
    onSuccess: () => {
      invalidateAll(client);
      toast.success(t('toast.imageReset'));
    },
    onError: (err) => toastError(err, t('toast.genericError')),
  });
}
